import React from 'react';
import { Link } from 'react-router-dom';
import {
  Accessibility, Keyboard, Eye, Volume2, MousePointerClick,
  ZoomIn, Languages, FileText, CheckCircle2, Mail, ChevronRight,
} from 'lucide-react';
import useSiteConfig from '../hooks/useSiteConfig';

/**
 * Página de Acessibilidade — declaração formal de conformidade com WCAG 2.1 AA.
 *
 * Apresentada para o edital municipal como prova auditável dos recursos
 * de acessibilidade já implementados na plataforma.
 */
export default function Acessibilidade() {
  const { config } = useSiteConfig();
  const contatoEmail = config?.email_contato || 'contato@empreendecomunidade.com.br';

  const recursos = [
    {
      icon: Keyboard,
      titulo: 'Navegação por teclado',
      texto:
        'Todos os elementos interativos (botões, links, formulários e cards) podem ser acessados pela tecla Tab, ativados com Enter ou barra de espaço. O foco visível usa contorno roxo de 3 px sobre o elemento ativo.',
    },
    {
      icon: ChevronRight,
      titulo: 'Link "Pular para o conteúdo"',
      texto:
        'No início de cada página há um link oculto que aparece ao receber foco, permitindo saltar o menu principal e ir direto ao conteúdo (atalho útil para leitores de tela).',
    },
    {
      icon: Eye,
      titulo: 'Contraste de cores',
      texto:
        'A paleta principal (roxo #56007F, amarelo #FFA52A, branco) atende ao mínimo WCAG AA de 4.5:1 para texto. Cards, badges e botões mantêm contraste consistente sobre os fundos claros e escuros do site.',
    },
    {
      icon: FileText,
      titulo: 'Texto alternativo em imagens',
      texto:
        'Logotipos de negócios, fotos de capa, badges e ícones decorativos seguem a convenção: imagens informativas têm atributo alt descritivo; ícones decorativos são marcados aria-hidden para não poluir o leitor de tela.',
    },
    {
      icon: Volume2,
      titulo: 'Semântica para leitores de tela',
      texto:
        'Estrutura com landmarks ARIA: header (role=banner), main, nav (role=navigation), footer (role=contentinfo). Modais usam role=dialog aria-modal e cards de negócio são article role=link com aria-label completo.',
    },
    {
      icon: MousePointerClick,
      titulo: 'Áreas de toque adequadas',
      texto:
        'Botões e ícones interativos respeitam o mínimo de 44×44 px (recomendação WCAG 2.5.5) para uso confortável em telas touch.',
    },
    {
      icon: ZoomIn,
      titulo: 'Zoom até 200%',
      texto:
        'O layout responsivo permite ampliar o navegador em até 200% sem perda de conteúdo ou funcionalidade. Texto reflui em colunas adaptativas.',
    },
    {
      icon: Languages,
      titulo: 'Idioma declarado',
      texto:
        'O documento HTML é servido com atributo lang="pt-BR" para que leitores de tela usem a pronúncia correta. Todos os textos da interface estão em português brasileiro claro e objetivo.',
    },
  ];

  const padroesAtendidos = [
    'WCAG 2.1 nível AA — Diretrizes para Conteúdo Web Acessível',
    'eMAG 3.1 — Modelo de Acessibilidade em Governo Eletrônico',
    'LGPD Art. 41 §1º — Disclosure público do Encarregado (DPO)',
    'NBR 17225 (em consulta) — Lei Brasileira de Inclusão',
  ];

  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="acessibilidade-page">
      {/* Hero */}
      <header className="bg-gradient-to-br from-[#56007F] to-[#7B2FA0] text-white">
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center backdrop-blur-sm">
              <Accessibility className="w-7 h-7" aria-hidden="true" />
            </div>
            <span className="text-xs uppercase tracking-widest text-white/80 font-semibold">
              Compromisso público
            </span>
          </div>
          <h1
            className="font-bold mb-3"
            style={{ fontFamily: 'Manrope', fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 1.1 }}
            data-testid="acessibilidade-title"
          >
            Acessibilidade na plataforma
          </h1>
          <p className="text-white/85 max-w-2xl text-base sm:text-lg">
            Nosso compromisso é que <strong>qualquer pessoa</strong>, com ou sem deficiência,
            consiga encontrar negócios locais, anunciar o próprio empreendimento e participar
            da economia da comunidade.
          </p>
          <div
            className="inline-flex items-center gap-2 mt-6 px-4 py-2 bg-[#FFA52A] text-[#3d005a] rounded-full text-sm font-bold shadow-lg"
            data-testid="wcag-badge-hero"
          >
            <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
            Conformidade WCAG 2.1 nível AA
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14 space-y-12">
        {/* Recursos disponíveis */}
        <section aria-labelledby="recursos-heading">
          <h2
            id="recursos-heading"
            className="text-2xl font-bold mb-6"
            style={{ fontFamily: 'Manrope' }}
          >
            Recursos disponíveis hoje
          </h2>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="recursos-grid">
            {recursos.map(({ icon: Icon, titulo, texto }) => (
              <article
                key={titulo}
                className="bg-white border border-[var(--border)] rounded-2xl p-5 hover:shadow-md transition-shadow"
                data-testid={`recurso-${titulo.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#56007F]/10 flex items-center justify-center text-[#56007F] shrink-0">
                    <Icon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-[var(--text-primary)] mb-1">
                      {titulo}
                    </h3>
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                      {texto}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Padrões */}
        <section aria-labelledby="padroes-heading">
          <h2
            id="padroes-heading"
            className="text-2xl font-bold mb-4"
            style={{ fontFamily: 'Manrope' }}
          >
            Padrões e legislações que seguimos
          </h2>
          <ul className="bg-white border border-[var(--border)] rounded-2xl divide-y divide-[var(--border)]">
            {padroesAtendidos.map((p) => (
              <li key={p} className="flex items-center gap-3 p-4">
                <CheckCircle2 className="w-5 h-5 text-[#16A34A] shrink-0" aria-hidden="true" />
                <span className="text-sm text-[var(--text-primary)]">{p}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Limitações conhecidas — transparência */}
        <section aria-labelledby="limites-heading">
          <h2
            id="limites-heading"
            className="text-2xl font-bold mb-3"
            style={{ fontFamily: 'Manrope' }}
          >
            Em evolução
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mb-4 max-w-3xl">
            Mantemos transparência sobre o que ainda pode ser melhorado. Os pontos abaixo
            estão no nosso roadmap público e são endereçados a cada sprint:
          </p>
          <ul className="space-y-2 text-sm text-[var(--text-primary)] list-disc list-inside pl-2">
            <li>Cobertura completa de legendas em vídeos de notícias incorporados.</li>
            <li>Modo de alto contraste opcional (toggle no perfil do usuário).</li>
            <li>Auditoria automatizada via axe-core integrada ao pipeline de testes.</li>
          </ul>
        </section>

        {/* Contato */}
        <section
          className="bg-[#56007F] text-white rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6"
          aria-labelledby="contato-heading"
        >
          <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center backdrop-blur-sm shrink-0">
            <Mail className="w-8 h-8" aria-hidden="true" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h2
              id="contato-heading"
              className="text-xl font-bold mb-1"
              style={{ fontFamily: 'Manrope' }}
            >
              Encontrou alguma barreira?
            </h2>
            <p className="text-sm text-white/85 mb-3">
              Queremos saber. Relate problemas de acessibilidade e nossa equipe responderá em até <strong>3 dias úteis</strong>.
            </p>
            <a
              href={`mailto:${contatoEmail}?subject=Relato%20de%20acessibilidade`}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FFA52A] text-[#3d005a] font-semibold rounded-full hover:bg-[#e89420] transition-colors"
              data-testid="acessibilidade-contato-btn"
            >
              <Mail className="w-4 h-4" aria-hidden="true" />
              Enviar relato por e-mail
            </a>
          </div>
        </section>

        {/* Links legais */}
        <nav className="text-center pt-4 border-t border-[var(--border)]" aria-label="Documentos relacionados">
          <p className="text-xs text-[var(--text-secondary)] mb-3">
            Veja também: documentos legais da plataforma
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
            <Link to="/termos-de-uso" className="text-[#56007F] hover:underline">Termos de Uso</Link>
            <span className="text-gray-300" aria-hidden="true">·</span>
            <Link to="/politica-de-privacidade" className="text-[#56007F] hover:underline">Política de Privacidade</Link>
            <span className="text-gray-300" aria-hidden="true">·</span>
            <Link to="/ajuda" className="text-[#56007F] hover:underline">Central de Ajuda</Link>
          </div>
        </nav>
      </div>
    </div>
  );
}
